import React from 'react';
import { Typography, Row, Col, Card, Button, Tabs } from 'antd';
import { Link } from 'react-router-dom';
import { 
  FileImageOutlined, 
  ArrowRightOutlined,
  CompressOutlined,
  FullscreenOutlined
} from '@ant-design/icons';
import { 
  getAvailableFormats, 
  ImageFormat, 
  getConversionInfo,
  getAvailableCompressionTypes,
  getAvailableResizeFormats
} from '../services/imageService';
import { uiLayoutMode } from '../uiConfig';
import { useLanguage } from '../i18n/LanguageContext';

const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;

// Define color schemes for each format (base color with variants)
const formatColorSchemes: Record<ImageFormat, string[]> = {
  webp: [
    '#e8f5e9',  // Lightest green
    '#c8e6c9',
    '#a5d6a7',
    '#81c784',  // Light green
    '#66bb6a',  // Medium green
    '#4caf50',  // Base green
    '#43a047',
    '#388e3c',  // Dark green
    '#2e7d32',
    '#1b5e20'   // Darkest green
  ],
  jpeg: [
    '#e3f2fd',  // Lightest blue
    '#bbdefb',
    '#90caf9',
    '#64b5f6',  // Light blue
    '#42a5f5',  // Medium blue
    '#2196f3',  // Base blue
    '#1e88e5',
    '#1976d2',  // Dark blue
    '#1565c0',
    '#0d47a1'   // Darkest blue
  ],
  jpg: [
    '#fff3e0',  // Lightest orange
    '#ffe0b2',
    '#ffcc80',
    '#ffb74d',  // Light orange
    '#ffa726',  // Medium orange
    '#ff9800',  // Base orange
    '#fb8c00',
    '#f57c00',  // Dark orange
    '#ef6c00',
    '#e65100'   // Darkest orange
  ],
  png: [
    '#f3e5f5',  // Lightest purple
    '#e1bee7',
    '#ce93d8',
    '#ba68c8',  // Light purple
    '#ab47bc',  // Medium purple
    '#9c27b0',  // Base purple
    '#8e24aa',
    '#7b1fa2',  // Dark purple
    '#6a1b9a',
    '#4a148c'   // Darkest purple
  ],
  ico: [
    '#ffebee',  // Lightest red
    '#ffcdd2',
    '#ef9a9a',
    '#e57373',  // Light red
    '#ef5350',  // Medium red
    '#f44336',  // Base red
    '#e53935',
    '#d32f2f',  // Dark red
    '#c62828',
    '#b71c1c'   // Darkest red
  ],
  bmp: [
    '#e0f7fa',  // Lightest cyan
    '#b2ebf2',
    '#80deea',
    '#4dd0e1',  // Light cyan
    '#26c6da',  // Medium cyan
    '#00bcd4',  // Base cyan
    '#00acc1',
    '#0097a7',  // Dark cyan
    '#00838f',
    '#006064'   // Darkest cyan
  ],
  apng: [
    '#efebe9',  // Lightest brown
    '#d7ccc8',
    '#bcaaa4',
    '#a1887f',  // Light brown
    '#8d6e63',  // Medium brown
    '#795548',  // Base brown
    '#6d4c41',
    '#5d4037',  // Dark brown
    '#4e342e',
    '#3e2723'   // Darkest brown
  ]
};

// Color palette for compression types
const compressionColorPalette = [
  '#673ab7',  // Deep Purple
  '#009688',  // Teal
  '#e91e63',  // Pink
];

// Color palette for resize types
const resizeColorPalette = [
  '#2196f3',  // Blue
  '#4caf50',  // Green
  '#ff9800',  // Orange
];

const HomePage: React.FC = () => {
  const { t } = useLanguage();
  const availableFormats = getAvailableFormats();
  const compressionTypes = getAvailableCompressionTypes(t);
  const resizeFormats = getAvailableResizeFormats(t);
  
  // Create a flat list of all conversion pairs grouped by source format
  const conversionPairsByFormat = availableFormats.map(format => ({
    from: format.from,
    pairs: format.to.map(toFormat => ({
      from: format.from,
      to: toFormat,
      info: getConversionInfo(format.from, toFormat, t)
    }))
  }));

  // Function to render the conversion cards
  const renderConversionCards = () => (
    <>
      {conversionPairsByFormat.map(({ from, pairs }) => {
        // Sort pairs to ensure a predictable order
        const sortedPairs = [...pairs].sort((a, b) => a.to.localeCompare(b.to));
        
        return (
          <div key={from} className="format-section">
            <Title level={3} style={{ marginTop: '24px', color: formatColorSchemes[from][5] }}>
              From {from.toUpperCase()}
            </Title>
            <Row gutter={[16, 16]}>
              {sortedPairs.map(({ to, info }, index) => {
                // Use colors from light to dark based on index
                // We use a modulo operation to wrap around if there are more items than colors
                const colorIndex = index % formatColorSchemes[from].length;
                const cardColor = formatColorSchemes[from][colorIndex];
                
                return (
                  <Col xs={24} sm={12} md={8} lg={6} key={`${from}-${to}`}>
                    <Link to={`/convert/${from}-to-${to}`}>
                      <Card 
                        hoverable
                        className="feature-card"
                        title={
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <FileImageOutlined style={{ marginRight: 8 }} />
                            {from.toUpperCase()} to {to.toUpperCase()}
                          </div>
                        }
                        headStyle={{ 
                          backgroundColor: cardColor,
                          color: colorIndex < 3 ? '#333' : 'white'
                        }}
                        style={{ 
                          borderTop: `0px`,
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                        bodyStyle={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <Paragraph style={{ flex: 1 }}>{info}</Paragraph>
                        <Button 
                          type="primary" 
                          block 
                          style={{ 
                            backgroundColor: cardColor, 
                            borderColor: cardColor,
                            color: colorIndex < 3 ? '#333' : 'white',
                            marginTop: 'auto'
                          }}
                        >
                          {t('common.convertNow')} <ArrowRightOutlined />
                        </Button>
                      </Card>
                    </Link>
                  </Col>
                );
              })}
            </Row>
          </div>
        );
      })}
    </>
  );

  // Function to render the compression cards
  const renderCompressionCards = () => (
    <Row gutter={[16, 16]} className="card-container">
      {compressionTypes.map((compression, index) => {
        // Use a different set of colors for compression cards
        const cardColor = compressionColorPalette[index % compressionColorPalette.length];
        
        return (
          <Col xs={24} sm={12} md={8} key={compression.type}>
            <Link to={`/compress/${compression.type}`}>
              <Card 
                hoverable
                className="feature-card"
                title={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CompressOutlined style={{ marginRight: 8 }} />
                    {compression.name}
                  </div>
                }
                headStyle={{ 
                  backgroundColor: cardColor,
                  color: 'white'
                }}
                style={{ 
                  borderTop: `0px`,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                bodyStyle={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Paragraph style={{ flex: 1 }}>{compression.description}</Paragraph>
                <Button 
                  type="primary" 
                  block 
                  style={{ 
                    backgroundColor: cardColor, 
                    borderColor: cardColor,
                    marginTop: 'auto'
                  }}
                >
                  {t('home.compress.now')} <CompressOutlined />
                </Button>
              </Card>
            </Link>
          </Col>
        )}
      )}
    </Row>
  );

  // Function to render the resize cards
  const renderResizeCards = () => (
    <Row gutter={[16, 16]} className="card-container">
      {resizeFormats.map((resize, index) => {
        // Use a different set of colors for resize cards
        const cardColor = resizeColorPalette[index % resizeColorPalette.length];
        
        return (
          <Col xs={24} sm={12} md={8} key={resize.type}>
            <Link to={`/resize/${resize.type}`}>
              <Card 
                hoverable
                className="feature-card"
                title={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FullscreenOutlined style={{ marginRight: 8 }} />
                    {resize.name}
                  </div>
                }
                headStyle={{ 
                  backgroundColor: cardColor,
                  color: 'white'
                }}
                style={{ 
                  borderTop: `0px`,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                bodyStyle={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Paragraph style={{ flex: 1 }}>{resize.description}</Paragraph>
                <Button 
                  type="primary" 
                  block 
                  style={{ 
                    backgroundColor: cardColor, 
                    borderColor: cardColor,
                    marginTop: 'auto'
                  }}
                >
                  {t('home.resize.now')} <FullscreenOutlined />
                </Button>
              </Card>
            </Link>
          </Col>
        )}
      )}
    </Row>
  );

  return (
    <div className="container">
      <div className="homepage-description">
        <Typography>
          <Title>{t('home.title')}</Title>
          <h3>{t('home.subtitle')}</h3>
          <h3>{t('home.subtitle2')}</h3>
        </Typography>
      </div>

      {uiLayoutMode === 'tabs' ? (
        <Tabs 
          defaultActiveKey="resize" 
          size="large" 
          className="responsive-tabs"
          tabBarStyle={{ fontWeight: 900 }}
        >
          <TabPane tab={t('home.resize.title')} key="resize">
            <Title level={2}>{t('home.resize.title')}</Title>
            {renderResizeCards()}
          </TabPane>
          <TabPane tab={t('home.convert.title')} key="convert">
            <Title level={2}>{t('home.convert.title')}</Title>
            {renderConversionCards()}
          </TabPane>
          <TabPane tab={t('home.compress.title')} key="compress">
            <Title level={2}>{t('home.compress.title')}</Title>
            {renderCompressionCards()}
          </TabPane>
        </Tabs>
      ) : (
        <>
          <section className="format-section">
            <Title level={2}>{t('home.resize.title')}</Title>
            {renderResizeCards()}
          </section>
          <section className="format-section">
            <Title level={2}>{t('home.convert.title')}</Title>
            {renderConversionCards()}
          </section>
          <section className="format-section">
            <Title level={2}>{t('home.compress.title')}</Title>
            {renderCompressionCards()}
          </section>
        </>
      )}

      <div className="ads-block">
        <div className="ads-content">
          <h1>
          {t('home.tools.description')}
          </h1>
          <br />
          <h2>
          {t('home.platform.description')}
          </h2>
        </div>
        <div className="ads-image">
          <img 
            src="/ads_1.png" 
            alt="Advertisement" 
          />
        </div>
      </div>

      <br/>

      <div className="ads-block">
        
        <div className="ads-image">
          <img 
            src="/ads_2.png" 
            alt="Advertisement" 
          />
        </div>

        <div className="ads-content">
          <h1>
          {t('home.simplify.tasks')}
          </h1>
          <br />
          <h2>
          {t('home.conversion.steps')}
          </h2>
        </div>
      </div>
    </div>
  );
};

export default HomePage; 